import { useState, useEffect } from 'react';
import { useBackendAuth } from '../contexts/BackendAuthContext';
import { Navigate, Link } from 'react-router-dom';
import { backendApiUrl } from '../config/backendApiUrl';

const BackendDashboardPage = () => {
  const { backendUser, backendToken, logout, isBackendAuthenticated } = useBackendAuth();
  const [crudData, setCrudData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redirect if not authenticated through backend
  if (!isBackendAuthenticated) {
    return <Navigate to="/backend-login" replace />;
  }

  useEffect(() => {
    const fetchCrudTest = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${backendApiUrl('/api')}/backend-auth/test/crud`, {
          headers: {
            Authorization: `Bearer ${backendToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCrudData(data);
          setError(null);
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to fetch data');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isBackendAuthenticated) {
      fetchCrudTest();
    }
  }, [backendToken, isBackendAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-blue-600 p-4 text-white">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">Backend Admin Dashboard</h1>
          <div className="flex items-center space-x-4">
            <span>
              Welcome, {backendUser?.username} ({backendUser?.role})
            </span>
            <button onClick={logout} className="px-3 py-1 bg-blue-700 hover:bg-blue-800 rounded">
              Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-6">
        <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">Backend Authentication Test</h2>
          <p className="mb-2">
            This page demonstrates a separate authentication system running in parallel to Firebase
            Auth.
          </p>
          <p className="mb-2">
            You can test CRUD operations and permissions while keeping the existing Firebase Auth
            system intact.
          </p>
          <div className="mt-4">
            <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 underline">
              Go to Firebase Auth Dashboard
            </Link>
          </div>
          <div className="mt-4">
            <Link to="/admin/api-test" className="text-blue-600 hover:text-blue-800 underline">
              Go to API Test
            </Link>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">User Information</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap">{JSON.stringify(backendUser, null, 2)}</pre>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-4">CRUD Test Results</h2>
          {loading ? (
            <p>Loading test data...</p>
          ) : error ? (
            <div className="text-red-500">{error}</div>
          ) : crudData ? (
            <div>
              <p className="mb-4">{crudData.message}</p>
              <h3 className="text-lg font-semibold mb-2">Items:</h3>
              <ul className="bg-gray-50 p-4 rounded-lg">
                {crudData.data?.items.map(item => (
                  <li key={item.id} className="mb-2 p-2 border-b">
                    ID: {item.id} - {item.name} (Created: {new Date(item.created).toLocaleString()})
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p>No data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackendDashboardPage;
